// Top KPI strip on the People Insights page. Each tile is clickable and
// surfaces a drill-panel context with relevant details.

import React from 'react';
import { Grid, Skeleton } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LogoutIcon from '@mui/icons-material/Logout';

import { formatNumberFull } from '../../utils/FormatUtils';
import type { HrStatsSummaryResponse } from '../../types/hrStats';
import KpiTile from './primitives/KpiTile';
import type { DrillContext } from './drillContextTypes';

export interface KpiStripProps {
  summary?: HrStatsSummaryResponse;
  loading: boolean;
  onOpenDrill: (ctx: DrillContext) => void;
}

const KpiStrip: React.FC<KpiStripProps> = ({ summary, loading, onOpenDrill }) => {
  if (loading || !summary) {
    return (
      <Grid container spacing={1.5}>
        {Array.from({ length: 6 }).map((_, idx) => (
          <Grid item xs={6} sm={4} md={2} key={idx}>
            <Skeleton variant="rounded" height={120} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const tiles = [
    {
      key: 'active',
      label: 'Active Headcount',
      value: formatNumberFull(summary.active_headcount),
      hint: `Total: ${formatNumberFull(summary.total_headcount)}`,
      icon: <PeopleAltIcon fontSize="small" />,
      accent: 'indigo' as const,
      onClick: () =>
        onOpenDrill({ kind: 'kpi', metric: 'active', label: 'Active Headcount' }),
    },
    {
      key: 'joiners',
      label: 'Joiners (90d)',
      value: formatNumberFull(summary.joiners_90d),
      hint: `30d: ${formatNumberFull(summary.joiners_30d)}`,
      icon: <PersonAddAlt1Icon fontSize="small" />,
      accent: 'emerald' as const,
      onClick: () =>
        onOpenDrill({ kind: 'kpi', metric: 'joiners', label: 'Joiners (90 days)' }),
    },
    {
      key: 'relieved',
      label: 'Relieved (90d)',
      value: formatNumberFull(summary.relieved_90d),
      hint: `30d: ${formatNumberFull(summary.relieved_30d)}`,
      icon: <LogoutIcon fontSize="small" />,
      accent: 'rose' as const,
      onClick: () =>
        onOpenDrill({
          kind: 'kpi',
          metric: 'relieved',
          label: 'Relieved (90 days)',
        }),
    },
    {
      key: 'tenure',
      label: 'Avg Tenure',
      value: `${summary.avg_tenure_years.toFixed(1)} yrs`,
      hint: `${summary.tenure_buckets.length} bands`,
      icon: <AccessTimeIcon fontSize="small" />,
      accent: 'amber' as const,
      onClick: () => onOpenDrill({ kind: 'kpi', metric: 'tenure', label: 'Avg Tenure' }),
    },
    {
      key: 'locations',
      label: 'Locations',
      value: formatNumberFull(summary.distinct_locations),
      hint: 'Distinct cities',
      icon: <PublicIcon fontSize="small" />,
      accent: 'cyan' as const,
      onClick: () =>
        onOpenDrill({ kind: 'kpi', metric: 'locations', label: 'Locations' }),
    },
    {
      key: 'org',
      label: 'BUs / Pillars',
      value: `${summary.distinct_business_units} / ${summary.distinct_pillars}`,
      hint: 'Org breadth',
      icon: <AccountTreeIcon fontSize="small" />,
      accent: 'violet' as const,
      onClick: () => onOpenDrill({ kind: 'kpi', metric: 'org', label: 'BUs / Pillars' }),
    },
  ];

  return (
    <Grid container spacing={1.5}>
      {tiles.map((t) => (
        <Grid item xs={6} sm={4} md={2} key={t.key}>
          <KpiTile
            label={t.label}
            value={t.value}
            hint={t.hint}
            icon={t.icon}
            accent={t.accent}
            onClick={t.onClick}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default KpiStrip;
